import React, { useCallback, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BreadcrumbItem, BreadcrumbLink } from "@chakra-ui/breadcrumb";
import { Box, HStack } from "@chakra-ui/layout";

import { BusinessBreadcrumbs } from "../../components/Breadcrumbs/BusinessBreadcrumbs";
import { PageContent } from "../../components/PageContent/PageContent";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { useBusinessContext } from "../../context/ModelContext";
import { ErrorMessage } from "../../components/ErrorMessage/ErrorMessage";
import { Checkbox } from "@chakra-ui/checkbox";
import { useCheckboxState } from "../../hooks/useCheckboxState";
import { LoadingIndicator } from "../../components/LoadingIndicator/LoadingIndicator";
import { useAdminApiClient } from "../../hooks/useApiClient";
import { CherryHubAdminApi } from "../../api/adminApiModels";
import { Button, SkeletonText, VStack } from "@chakra-ui/react";
import { PlusIcon } from "../../styles/icons";
import { useToast } from "../../hooks/useToast";
import { useAdminApiUpdateRequest } from "../../hooks/useApiUpdatedRequest";
import { useConfirmationDialog } from "../../hooks/useConfirmationDialog";
import { usePaginatedAdminApiRequest } from "../../hooks/usePaginatedApiRequest";
import { IntegrationsTable } from "../../components/Integration/IntegrationsTable";
import { useSearchInput } from "../../hooks/useSearchInput";
import { BusinessMenu } from "../../components/Business/BusinessMenu";

export const Integrations = () => {
  const business = useBusinessContext();
  const apiClient = useAdminApiClient();
  const navigate = useNavigate();
  const {
    checked: showDeletedIntegrations,
    onChange: onChangeDeletedIntegrationCheck,
  } = useCheckboxState(false);

  const { showErrorToast, showSuccessToast } = useToast();
  const { confirmationElement, showConfirmationDialog } =
    useConfirmationDialog();

  const { debouncedQuery, element: searchInputElement } = useSearchInput({
    placeholder: "Search integration...",
  });

  const {
    items: integrations,
    refresh,
    isLoading,
    isLoadingPage,
    error,
    fetchNextPage,
  } = usePaginatedAdminApiRequest(
    // Request the first page
    (apiClient) =>
      apiClient.getBusinessIntegrations(business.BusinessId!, {
        q: debouncedQuery,
        includeDeleted: showDeletedIntegrations ? "true" : "false",
      }),
    // Request subsequent pages
    (apiClient, _, continuationToken) =>
      apiClient.getBusinessIntegrations(business.BusinessId!, {
        q: debouncedQuery,
        includeDeleted: showDeletedIntegrations ? "true" : "false",
        continuationToken,
      }),
    [business.BusinessId, debouncedQuery, showDeletedIntegrations]
  );

  const deleteIntegrationRequest = useAdminApiUpdateRequest(
    (apiClient, integrationId: string) =>
      apiClient.deleteBusinessIntegration(business.BusinessId!, integrationId),
    [integrations]
  );

  const restoreIntegrationRequest = useAdminApiUpdateRequest(
    (apiClient, integrationId: string) =>
      apiClient.restoreBusinessIntegration(business.BusinessId!, integrationId),
    [integrations]
  );

  const onClickNewIntegration = () =>
    navigate(`/admin/businesses/${business.BusinessId}/integrations/new`);

  const onClickEditIntegration = (
    integration: CherryHubAdminApi.IntegrationSummaryItem
  ) =>
    navigate(
      `/admin/businesses/${business.BusinessId}/integrations/${integration.Meta?.Id}`
    );

  useEffect(() => {
    if (deleteIntegrationRequest.result) {
      if (deleteIntegrationRequest.result.ok) {
        showSuccessToast("Integration deleted.");
        refresh();
      } else {
        showErrorToast(
          deleteIntegrationRequest.result.message ??
            "Failed to delete integration."
        );
      }
    }
  }, [deleteIntegrationRequest.result, refresh]);

  const onClickDeleteIntegration = useCallback(
    (integration: CherryHubAdminApi.IntegrationSummaryItem) =>
      showConfirmationDialog(
        "Delete Integration?",
        `Are you sure you wish to delete this integration: ${integration.DisplayName}?`,
        "Delete"
      ).then(({ ok }) => {
        if (ok) {
          deleteIntegrationRequest.request(integration.Meta?.Id!);
        }
      }),
    [
      showSuccessToast,
      showErrorToast,
      showConfirmationDialog,
      apiClient,
      refresh,
    ]
  );

  useEffect(() => {
    if (restoreIntegrationRequest.result) {
      if (restoreIntegrationRequest.result.ok) {
        showSuccessToast("Deleted integration restored.");
        refresh();
      } else {
        showErrorToast(
          restoreIntegrationRequest.result.message ??
            "Failed to restore deleted integration."
        );
      }
    }
  }, [restoreIntegrationRequest.result, refresh]);

  const onClickRestoreIntegration = useCallback(
    (integration: CherryHubAdminApi.IntegrationSummaryItem) =>
      showConfirmationDialog(
        "Restore Integration?",
        `Are you sure you wish to restore this deleted integration: ${integration.DisplayName}?`,
        "Restore"
      ).then(({ ok }) => {
        if (ok) {
          restoreIntegrationRequest.request(integration.Meta?.Id!);
        }
      }),
    [
      showSuccessToast,
      showErrorToast,
      showConfirmationDialog,
      apiClient,
      refresh,
    ]
  );

  return (
    <>
      <PageHeading>
        <PageHeading.Title>Integrations</PageHeading.Title>
      </PageHeading>
      <PageContent>
        <VStack w="100%" alignItems="flex-start">
          <BusinessBreadcrumbs variant="pageHeading" mb={4}>
            <BreadcrumbItem>
              <BreadcrumbLink
                as={Link}
                to={`/admin/businesses/${business.BusinessId}/integrations`}
              >
                Integrations
              </BreadcrumbLink>
            </BreadcrumbItem>
          </BusinessBreadcrumbs>

          <HStack w="100%">
            <Box flex="4" maxWidth="400px">
              {searchInputElement}
            </Box>
            <Box flex="1">
              <Checkbox
                checked={showDeletedIntegrations}
                onChange={onChangeDeletedIntegrationCheck}
                title="Include deleted integration in the list."
              >
                Include Deleted
              </Checkbox>
            </Box>
            <Box marginLeft="auto">
              <BusinessMenu
                businessId={business.Meta?.Id!}
                leftIcon={<PlusIcon />}
                onClick={onClickNewIntegration}
              >
                New Integration
              </BusinessMenu>
            </Box>
          </HStack>

          <IntegrationsTable
            integrations={integrations}
            isLoading={isLoading}
            disableActions={deleteIntegrationRequest.isLoading}
            debouncedQuery={debouncedQuery}
            onClickEditIntegration={onClickEditIntegration}
            onClickDeleteIntegration={onClickDeleteIntegration}
            onClickRestoreIntegration={onClickRestoreIntegration}
          />
          {fetchNextPage && !isLoading && (
            <Box w="100%">
              {!isLoadingPage && (
                <Button colorScheme="cherryButton" onClick={fetchNextPage}>
                  Load more...
                </Button>
              )}
              {isLoadingPage && (
                <HStack width="100%" boxShadow="lg">
                  <Box width="90%" padding="4" pr="0">
                    <SkeletonText mt="4" noOfLines={4} spacing="4" />
                  </Box>
                  <Box width="5%" pt="4" pb="4">
                    <SkeletonText mt="4" noOfLines={4} spacing="4" />
                  </Box>
                  <Box width="5%" pt="4" pb="4" pr="4">
                    <SkeletonText mt="4" noOfLines={4} spacing="4" />
                  </Box>
                </HStack>
              )}
            </Box>
          )}
          {error != null && (
            <ErrorMessage>
              {error ??
                "An error was encountered while searching integrations."}
            </ErrorMessage>
          )}
          {(!integrations || isLoading) && <LoadingIndicator />}
          {confirmationElement}
        </VStack>
      </PageContent>
    </>
  );
};
